"use client";

import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import Script from "next/script";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { AdminFormSection } from "@/components/admin/AdminFormSection";
import { ImageUpload } from "@/components/admin/ImageUpload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Zap, ArrowRight, Check, Eye, EyeOff, ChevronDown, ChevronRight } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import api from "@/client/api/axios";
import { useCategoriesQuery } from "@/hooks/useCategories";
import { useSubCategoriesQuery } from "@/hooks/useSubCategories";
import { useCitiesQuery } from "@/hooks/useCities";
import { getGoogleMapsScriptUrl, isGoogleMapsLoaded, shouldInjectGoogleMapsScript } from "@/lib/google-maps-loader";

type RegistrationStep = "category" | "business" | "credentials";

interface BusinessData {
  businessName: string;
  businessType: "business" | "contractor" | "individual";
  startDate: string;
  logo: string | null;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  categoryDetails: string;
  selectedCategoryIds: number[];
  selectedSubCategoryIds: number[];
  firstName: string;
  lastName: string;
}

interface CredentialsData {
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

export default function VendorRegister() {
  const router = useRouter();
  const pathname = usePathname();
  const { signupVendor } = useAuthStore();
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const { data: cities = [], isLoading: citiesLoading } = useCitiesQuery("active");
  const { data: categories = [], isLoading: categoriesLoading } = useCategoriesQuery(
    selectedCityId != null ? { status: "ACTIVE", cityId: selectedCityId } : { status: "ACTIVE", cityId: -1 }
  );
  const { data: subCategories = [] } = useSubCategoriesQuery(
    selectedCityId != null ? { status: "active", cityId: selectedCityId } : { status: "active", cityId: -1 }
  );
  const subCategoriesByCategory = useMemo(() => {
    const map: Record<number, typeof subCategories> = {};
    for (const sc of subCategories) {
      if (!map[sc.categoryId]) map[sc.categoryId] = [];
      map[sc.categoryId].push(sc);
    }
    return map;
  }, [subCategories]);

  const [currentStep, setCurrentStep] = useState<RegistrationStep>("category");
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);
  const [mapsReady, setMapsReady] = useState(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFieldId, setUploadingFieldId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [businessData, setBusinessData] = useState<BusinessData>({
    businessName: "",
    businessType: "business",
    startDate: "",
    logo: null,
    address: "",
    city: "",
    state: "",
    zipCode: "",
    categoryDetails: "",
    selectedCategoryIds: [],
    selectedSubCategoryIds: [],
    firstName: "",
    lastName: "",
  });

  const [credentialsData, setCredentialsData] = useState<CredentialsData>({
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const selectedCity = useMemo(
    () => cities.find((c) => c.id === selectedCityId),
    [cities, selectedCityId]
  );

  useEffect(() => {
    setBusinessData((prev) => ({ ...prev, selectedSubCategoryIds: [] }));
    setExpandedCategoryIds([]);
  }, [selectedCityId]);

  const googleMapsApiKey =
    typeof window !== "undefined"
      ? (process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
         process.env.NEXT_PUBLIC_GOOGLE_API_KEY)
      : process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
        process.env.NEXT_PUBLIC_GOOGLE_API_KEY;
  const mapsApiKey = typeof googleMapsApiKey === "string" ? googleMapsApiKey.trim() : googleMapsApiKey;
  const injectMapsScript = !!mapsApiKey && shouldInjectGoogleMapsScript();

  useLayoutEffect(() => {
    if (injectMapsScript && mapsApiKey && !isGoogleMapsLoaded()) {
      (window as unknown as Record<string, () => void>).__googleMapsReady = () => {
        if (isGoogleMapsLoaded()) {
          setMapsReady(true);
          delete (window as unknown as Record<string, unknown>).__googleMapsReady;
        }
      };
    }
  }, [injectMapsScript, mapsApiKey]);

  useEffect(() => {
    if (!mapsApiKey) return;
    if (isGoogleMapsLoaded()) {
      setMapsReady(true);
      return;
    }
    let id: ReturnType<typeof setInterval> | null = null;
    const checkLoaded = () => {
      if (isGoogleMapsLoaded()) {
        setMapsReady(true);
        if (id) clearInterval(id);
      }
    };
    id = setInterval(checkLoaded, 100);
    return () => {
      if (id) clearInterval(id);
    };
  }, [mapsApiKey, injectMapsScript]);

  useEffect(() => {
    if (
      currentStep !== "business" ||
      !mapsReady ||
      !addressInputRef.current ||
      !window.google?.maps?.places
    )
      return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      { types: ["address"], fields: ["address_components", "formatted_address"] }
    );
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const addr = place.address_components || [];
      let city = "";
      let state = "";
      let zipCode = "";
      let streetNumber = "";
      let route = "";

      for (const c of addr) {
        if (c.types.includes("locality")) city = c.long_name;
        else if (c.types.includes("administrative_area_level_2") && !city) city = c.long_name;
        else if (c.types.includes("administrative_area_level_1")) state = c.long_name;
        else if (c.types.includes("postal_code")) zipCode = c.long_name;
        else if (c.types.includes("street_number")) streetNumber = c.long_name;
        else if (c.types.includes("route")) route = c.long_name;
      }

      const addressLine =
        place.formatted_address ||
        [streetNumber, route].filter(Boolean).join(" ").trim() ||
        "";

      setBusinessData((prev) => ({
        ...prev,
        address: addressLine || prev.address,
        city: city || prev.city,
        state: state || prev.state,
        zipCode: zipCode || prev.zipCode,
      }));
    });

    return () => {
      if (listener && window.google?.maps?.event)
        window.google.maps.event.removeListener(listener);
      autocompleteRef.current = null;
    };
  }, [currentStep, mapsReady]);

  const validateCategory = () => {
    if (selectedCityId == null) return "Please select a city";
    if (businessData.selectedSubCategoryIds.length === 0)
      return "Select at least one subcategory";
    return "";
  };

  const validateBusiness = () => {
    if (!businessData.firstName.trim()) return "First name is required";
    if (!businessData.lastName.trim()) return "Last name is required";
    if (!businessData.businessName.trim()) return "Business name is required";
    if (!businessData.startDate) return "Business start date is required";
    if (!businessData.address.trim()) return "Address is required";
    if (!businessData.city.trim()) return "City is required";
    if (!businessData.state.trim()) return "State is required";
    if (!businessData.zipCode.trim()) return "Zip code is required";
    if (businessData.selectedCategoryIds.length === 0)
      return "Select at least one service category";
    return "";
  };

  const validateCredentials = () => {
    if (!credentialsData.email.trim()) return "Email is required";
    if (!credentialsData.email.includes("@")) return "Valid email is required";
    if (!credentialsData.phone.trim()) return "Phone number is required";
    if (!credentialsData.password) return "Password is required";
    if (credentialsData.password.length < 8)
      return "Password must be at least 8 characters";
    if (credentialsData.password !== credentialsData.confirmPassword)
      return "Passwords do not match";
    return "";
  };

  const handleLogoUpload = async (file: File): Promise<{ url: string }> => {
    setUploadingFieldId("logo");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/auth/vendor-register/upload-logo", formData);
      const url = res.data?.data?.url ?? res.data?.url;
      if (url) {
        setBusinessData((prev) => ({ ...prev, logo: url }));
        toast.success("Logo uploaded successfully");
        return { url };
      }
      throw new Error("No URL returned");
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ?? "Could not upload logo. Please try again.";
      toast.error("Upload failed", { description: msg });
      throw err;
    } finally {
      setUploadingFieldId(null);
    }
  };

  const getApiErrorMessage = (err: any) => {
    const apiError = err?.response?.data?.error;
    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (err?.response?.status === 401) return "Please sign in to continue.";
    return "Could not create account. Please try again.";
  };

  const handleNextStep = async () => {
    let validationError = "";

    if (currentStep === "category") {
      validationError = validateCategory();
      if (!validationError) {
        setBusinessData((prev) => {
          const catIds = [...new Set(
            prev.selectedSubCategoryIds
              .map((subId) => subCategories.find((s) => s.id === subId)?.categoryId)
              .filter((id): id is number => id != null)
          )];
          return {
            ...prev,
            ...(selectedCity && {
              city: selectedCity.name,
              state: selectedCity.state || prev.state,
            }),
            selectedCategoryIds: catIds,
          };
        });
        setCurrentStep("business");
      }
    } else if (currentStep === "business") {
      validationError = validateBusiness();
      if (!validationError) setCurrentStep("credentials");
    } else if (currentStep === "credentials") {
      validationError = validateCredentials();
      if (!validationError) {
        setIsSubmitting(true);
        try {
          const combinedData = {
            ...businessData,
            ...credentialsData,
            selectedCityId: selectedCityId ?? undefined,
          };
          const result = await signupVendor(combinedData);
          if (result.requiresVerification && result.userId) {
            const verifyParams = new URLSearchParams({
              email: result.email ?? credentialsData.email,
              userId: String(result.userId),
              returnUrl: "/vendor/dashboard",
            });
            toast.success("Vendor account created", {
              description: "Please verify your email to continue.",
            });
            router.push(`/verifyemail?${verifyParams.toString()}`);
          } else {
            toast.success("Vendor account created", {
              description: "Your account has been created successfully.",
            });
            router.push("/vendor/dashboard");
          }
        } catch (err: any) {
          toast.error(getApiErrorMessage(err));
        } finally {
          setIsSubmitting(false);
        }
      }
    }

    if (validationError) toast.error(validationError);
  };

  const handlePreviousStep = () => {
    if (currentStep === "business") setCurrentStep("category");
    else if (currentStep === "credentials") setCurrentStep("business");
  };

  const toggleCategoryExpanded = (categoryId: number) => {
    setExpandedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleSubCategorySelection = (subCategoryId: number) => {
    setBusinessData((prev) => {
      const has = prev.selectedSubCategoryIds.includes(subCategoryId);
      return {
        ...prev,
        selectedSubCategoryIds: has
          ? prev.selectedSubCategoryIds.filter((id) => id !== subCategoryId)
          : [...prev.selectedSubCategoryIds, subCategoryId],
      };
    });
  };

  const progressPercentage =
    currentStep === "category"
      ? 33
      : currentStep === "business"
        ? 66
        : 100;

  return (
    <div className="min-h-screen bg-background">
      {mapsApiKey && injectMapsScript && (
        <Script
          src={getGoogleMapsScriptUrl(mapsApiKey)}
          strategy="afterInteractive"
        />
      )}
      {}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-2 hover:opacity-80 transition"
          >
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground">
              VendorCall
            </span>
          </Link>
          <Link href={pathname ? `/login?returnUrl=${encodeURIComponent(pathname)}` : "/login"}>
            <Button variant="ghost" className="text-foreground">
              Sign In
            </Button>
          </Link>
        </nav>
      </header>

      {}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {}
          <div className="md:col-span-1">
            <Card className="p-6 sticky top-24">
              <div className="space-y-6">
                {}
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3">
                    {progressPercentage}% complete
                  </p>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                {}
                <div className="space-y-3">
                  {["category", "business", "credentials"].map((step, idx) => {
                    const stepNames = [
                      "Category Details",
                      "Business Details",
                      "Login Details",
                    ];
                    const isCompleted =
                      (step === "category" && currentStep !== "category") ||
                      (step === "business" && currentStep === "credentials");
                    const isActive = step === currentStep;

                    return (
                      <div key={step} className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                            isActive
                              ? "bg-primary text-primary-foreground"
                              : isCompleted
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-foreground/60"
                          }`}
                        >
                          {isCompleted ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            idx + 1
                          )}
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            isActive
                              ? "text-primary"
                              : isCompleted
                                ? "text-primary"
                                : "text-foreground/50"
                          }`}
                        >
                          {stepNames[idx]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          </div>

          {}
          <div className="md:col-span-3">
            <Card className="p-8">
              <>
                {}
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                      Register as a Service Provider
                    </h1>
                    <p className="text-foreground/70">
                      Please fill in the details below, and our support team
                      will get back to you soon!
                    </p>
                  </div>

                  {}
                  {currentStep === "category" && (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-foreground">
                          City <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={selectedCityId != null ? String(selectedCityId) : ""}
                          onValueChange={(v) => setSelectedCityId(v ? parseInt(v, 10) : null)}
                        >
                          <SelectTrigger className="h-8 w-full max-w-xs">
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                          <SelectContent>
                            {citiesLoading ? (
                              <SelectItem value="__loading__" disabled>Loading cities...</SelectItem>
                            ) : cities.length === 0 ? (
                              <SelectItem value="__empty__" disabled>No cities available</SelectItem>
                            ) : (
                              cities.map((city) => (
                                <SelectItem key={city.id} value={String(city.id)}>
                                  {city.name}, {city.state}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedCityId != null && (
                        <div>
                          <Label className="text-sm font-medium text-foreground mb-3 block">
                            Select category <span className="text-destructive">*</span>
                          </Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Click a category to open its subcategories, then select the subcategories you offer one by one.
                          </p>
                          <div className="space-y-1 max-h-80 overflow-y-auto pr-2">
                            {categoriesLoading ? (
                              <p className="text-muted-foreground text-sm">Loading categories...</p>
                            ) : categories.length === 0 ? (
                              <p className="text-muted-foreground text-sm">No categories available for this city.</p>
                            ) : (
                              categories.map((category) => {
                                const subs = subCategoriesByCategory[category.id] ?? [];
                                const isExpanded = expandedCategoryIds.includes(category.id);
                                return (
                                  <div key={category.id} className="border border-border rounded-md overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => toggleCategoryExpanded(category.id)}
                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-muted/50 hover:bg-muted transition"
                                    >
                                      {subs.length > 0 ? (
                                        isExpanded ? (
                                          <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
                                        )
                                      ) : (
                                        <span className="w-4" />
                                      )}
                                      <span className="text-foreground font-medium">{category.name}</span>
                                      {subs.length > 0 && (
                                        <span className="text-xs text-muted-foreground ml-auto">
                                          {subs.length} subcategor{subs.length === 1 ? "y" : "ies"}
                                        </span>
                                      )}
                                    </button>
                                    {subs.length > 0 && isExpanded && (
                                      <div className="pl-6 pr-3 py-2 space-y-2 bg-background border-t border-border">
                                        {subs.map((sub) => (
                                          <label
                                            key={sub.id}
                                            className="flex items-center gap-3 cursor-pointer py-1"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={businessData.selectedSubCategoryIds.includes(sub.id)}
                                              onChange={() => toggleSubCategorySelection(sub.id)}
                                              className="w-4 h-4 rounded"
                                            />
                                            <span className="text-foreground text-sm">{sub.name}</span>
                                          </label>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {}
                  {currentStep === "business" && (
                    <div className="space-y-6">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="vendor-first-name" className="text-sm font-medium text-foreground">
                            First Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="vendor-first-name"
                            type="text"
                            placeholder="First Name"
                            value={businessData.firstName}
                            onChange={(e) =>
                              setBusinessData({ ...businessData, firstName: e.target.value })
                            }
                            className="h-8"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="vendor-last-name" className="text-sm font-medium text-foreground">
                            Last Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="vendor-last-name"
                            type="text"
                            placeholder="Last Name"
                            value={businessData.lastName}
                            onChange={(e) =>
                              setBusinessData({ ...businessData, lastName: e.target.value })
                            }
                            className="h-8"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="vendor-business-name" className="text-sm font-medium text-foreground">
                            Business Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="vendor-business-name"
                            type="text"
                            placeholder="Enter business name"
                            value={businessData.businessName}
                            onChange={(e) =>
                              setBusinessData({
                                ...businessData,
                                businessName: e.target.value,
                              })
                            }
                            className="h-8"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="vendor-start-date" className="text-sm font-medium text-foreground">
                            Business Start Date <span className="text-destructive">*</span>
                          </Label>
                          <DatePicker
                            id="vendor-start-date"
                            value={businessData.startDate}
                            onChange={(startDate) =>
                              setBusinessData((prev) => ({
                                ...prev,
                                startDate,
                              }))
                            }
                            placeholder="Select business start date"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-foreground mb-3 block">
                          Business Type <span className="text-destructive">*</span>
                        </Label>
                        <div className="space-y-2">
                          {[
                            { value: "business", label: "Business" },
                            {
                              value: "contractor",
                              label: "Independent Contractor",
                            },
                            { value: "individual", label: "Individual" },
                          ].map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="businessType"
                                value={option.value}
                                checked={
                                  businessData.businessType === option.value
                                }
                                onChange={(e) =>
                                  setBusinessData({
                                    ...businessData,
                                    businessType: e.target.value as any,
                                  })
                                }
                                className="w-4 h-4"
                              />
                              <span className="text-foreground">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <AdminFormSection title="Business Logo">
                        <p className="text-xs text-muted-foreground mb-4">
                          Recommended: JPG, PNG or WebP. Image size should be
                          less than 1MB.
                        </p>
                        <div className="w-full">
                          <div className="rounded-lg border border-border bg-card p-4 shadow-sm flex flex-row items-center gap-4 w-full">
                            <div className="min-w-0 flex-1">
                              <Label
                                htmlFor="vendor-logo"
                                className="text-sm font-medium"
                              >
                                Business Logo
                              </Label>
                              <p className="mt-1 text-[11px] text-muted-foreground">
                                Logo displayed for your business.
                              </p>
                            </div>
                            <div className="shrink-0">
                              <ImageUpload
                                id="vendor-logo"
                                label=""
                                required={false}
                                value={businessData.logo ?? ""}
                                onChange={(url) =>
                                  setBusinessData((prev) => ({
                                    ...prev,
                                    logo: url || null,
                                  }))
                                }
                                onUpload={handleLogoUpload}
                                isUploading={uploadingFieldId === "logo"}
                                onUploadError={(msg) =>
                                  toast.error("Upload failed", {
                                    description: msg,
                                  })
                                }
                                className="gap-2"
                              />
                            </div>
                          </div>
                        </div>
                      </AdminFormSection>

                      <div className="space-y-2">
                        <Label htmlFor="vendor-address" className="text-sm font-medium text-foreground">
                          Address <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="vendor-address"
                          ref={addressInputRef}
                          type="text"
                          placeholder="Start typing for address suggestions"
                          value={businessData.address}
                          onChange={(e) =>
                            setBusinessData({
                              ...businessData,
                              address: e.target.value,
                            })
                          }
                          className="h-8"
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          Select an address from the dropdown to auto-fill city, state, and zip code.
                        </p>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="vendor-city" className="text-sm font-medium text-foreground">
                            City <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="vendor-city"
                            type="text"
                            placeholder="City"
                            value={businessData.city}
                            onChange={(e) =>
                              setBusinessData({
                                ...businessData,
                                city: e.target.value,
                              })
                            }
                            className="h-8"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="vendor-state" className="text-sm font-medium text-foreground">
                            State <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="vendor-state"
                            type="text"
                            placeholder="State"
                            value={businessData.state}
                            onChange={(e) =>
                              setBusinessData({
                                ...businessData,
                                state: e.target.value,
                              })
                            }
                            className="h-8"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="vendor-zip" className="text-sm font-medium text-foreground">
                            Zip Code <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="vendor-zip"
                            type="text"
                            placeholder="Zip code"
                            value={businessData.zipCode}
                            onChange={(e) =>
                              setBusinessData({
                                ...businessData,
                                zipCode: e.target.value,
                              })
                            }
                            className="h-8"
                            required
                          />
                        </div>
                      </div>

                    </div>
                  )}

                  {}
                  {currentStep === "credentials" && (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="vendor-email" className="text-sm font-medium text-foreground">
                          Email <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="vendor-email"
                          type="email"
                          placeholder="Enter email address"
                          value={credentialsData.email}
                          onChange={(e) =>
                            setCredentialsData({ ...credentialsData, email: e.target.value })
                          }
                          className="h-8"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vendor-password" className="text-sm font-medium text-foreground">
                          Password <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="vendor-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter password"
                            value={credentialsData.password}
                            onChange={(e) =>
                              setCredentialsData({
                                ...credentialsData,
                                password: e.target.value,
                              })
                            }
                            className="h-8 pr-9"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Passwords must contain at least six characters including 1 uppercase, 1 lowercase, a number, and a special character.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="vendor-confirm-password" className="text-sm font-medium text-foreground">
                          Confirm Password <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="vendor-confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm password"
                            value={credentialsData.confirmPassword}
                            onChange={(e) =>
                              setCredentialsData({
                                ...credentialsData,
                                confirmPassword: e.target.value,
                              })
                            }
                            className="h-8 pr-9"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="vendor-phone" className="text-sm font-medium text-foreground">
                          Mobile <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="vendor-phone"
                          type="tel"
                          placeholder="Enter mobile number"
                          value={credentialsData.phone}
                          onChange={(e) =>
                            setCredentialsData({ ...credentialsData, phone: e.target.value })
                          }
                          className="h-8"
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          You must be able to receive text messages at this number to complete the application.
                        </p>
                      </div>

                      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                        <p className="text-sm text-foreground/70">
                          By creating an account, you agree to our Terms of
                          Service and Privacy Policy.
                        </p>
                      </div>
                    </div>
                  )}

                {}
                  <div className="flex gap-2 justify-end pt-4 mt-8 border-t border-border">
                    <Button
                      type="button"
                      onClick={handlePreviousStep}
                      disabled={currentStep === "category"}
                      variant="outline"
                      size="sm"
                      className="h-7"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleNextStep}
                      disabled={isSubmitting}
                      size="sm"
                      className="h-7 bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
                    >
                      {currentStep === "credentials" ? (
                        isSubmitting ? "Creating…" : "Create Account"
                      ) : (
                        <>
                          Next <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </div>
              </>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
